import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PaymentInputsWrapper, usePaymentInputs } from 'react-payment-inputs';
import images from 'react-payment-inputs/images';
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import SubscriptionApiService from '../../../api/SubscriptionApiService';
import AccountApiService from '../../../api/AccountApiService';
import CardPaymentDto from '../../../DTOs/Subscription/CardPaymentDto';
import PayBalanceRequestDto from '../../../DTOs/Ledger/PayBalanceRequestDto';
import AppDialog from '../AppDialog';
import * as Constants from '../../../utilities/Constants';

const NEW_PAYMENT_METHOD_ID = 0;

/**
 * Settles a tenant's outstanding balance.
 *
 * Close in shape to ChangePaymentModal on purpose - the same card list, the same inputs - because a
 * tenant arriving here from a failed payment has usually just been through that one. The difference
 * is that this takes money, so it states the amount plainly and keeps saying it on the button.
 *
 * Entering a new card is offered alongside the saved ones rather than behind a separate flow. The
 * tenant most likely to be looking at this screen is one whose only card expired, and they have
 * nothing on file worth choosing.
 */
export default function PayBalanceModal(props) {
    const { open, close, statement, userId, isMobile, setSnackbarProperties, onPaid } = props;

    const [paymentOptions, setPaymentOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState();
    const [cardNumber, setCardNumber] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [cvc, setCvc] = useState('');
    const [saveAsDefault, setSaveAsDefault] = useState(true);
    const [loading, setLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState('');

    const {
        wrapperProps,
        getCardImageProps,
        getCardNumberProps,
        getExpiryDateProps,
        getCVCProps,
    } = usePaymentInputs();

    const subscriptionApiService = new SubscriptionApiService();
    const accountApiService = new AccountApiService();

    const isNewCardSelected = useMemo(
        () => selectedOption === NEW_PAYMENT_METHOD_ID,
        [selectedOption]
    );

    const amount = statement?.balance ?? 0;

    async function loadPaymentMethods() {
        const methods = await subscriptionApiService.getUserPaymentMethods(userId);
        const usable = (methods.data ?? []);

        setPaymentOptions(usable);

        // Preselect the first card that can actually be charged. Landing on an expired one means the
        // tenant has to notice the disabled submit button and work out why.
        const firstUsable = usable.find((option) => !option.isExpired);

        setSelectedOption(firstUsable ? firstUsable.paymentProfileId : NEW_PAYMENT_METHOD_ID);
    }

    async function handlePay() {
        setPaying(true);
        setError('');

        const request = new PayBalanceRequestDto();

        // Sent so the server can refuse if its own figure has moved. The tenant agreed to this
        // number; the screen may have been open across a billing run since.
        request.expectedAmount = amount;

        if (isNewCardSelected) {
            const card = new CardPaymentDto();
            card.cardNumber = cardNumber;
            card.cardExpiration = expirationDate;
            card.cvv = cvc;

            request.newCard = card;
            request.saveAsDefault = saveAsDefault;
        } else {
            request.paymentProfileId = selectedOption;
            request.saveAsDefault = false;
        }

        const response = await accountApiService.payBalance(request);

        setPaying(false);

        if (!response.success) {
            // Kept inside the dialog rather than raised as a snackbar. Every failure here is one the
            // tenant can act on without leaving the screen - a different card, or the same one
            // again - and closing the dialog to show the reason would lose what they had entered.
            setError(response.message);

            return;
        }

        setSnackbarProperties(
            `Thank you. We have taken ${Constants.USDollar.format(response.data.amountPaid)}.`
        );

        closeModal();
        onPaid();
    }

    function closeModal() {
        setCardNumber('');
        setExpirationDate('');
        setCvc('');
        setError('');
        setSaveAsDefault(true);
        close();
    }

    useEffect(() => {
        if (open) {
            setLoading(true);
            setError('');
            loadPaymentMethods().finally(() => setLoading(false));
        }
    }, [open]);

    const newCardEntered = cardNumber.length > 0 && expirationDate.length > 0 && cvc.length > 0;
    const canSubmit = isNewCardSelected ? newCardEntered : Boolean(selectedOption);

    const newCardInputs = isMobile
        ? <div>
            {cardNumber.length > 0 && <svg className={'mobile-card-image'} {...getCardImageProps({ images })} />}
            <input className={'mobile-card-number-input'} {...getCardNumberProps({ onChange: (event) => setCardNumber(event.target.value) })} value={cardNumber} />
            <input className={'mobile-exp-date-input'} {...getExpiryDateProps({ onChange: (event) => setExpirationDate(event.target.value) })} value={expirationDate} />
            <input className={'mobile-cvc-input'} {...getCVCProps({ onChange: (event) => setCvc(event.target.value) })} value={cvc} />
        </div>
        : <PaymentInputsWrapper className={'change-payment-card-input-shown'} {...wrapperProps}>
            {cardNumber.length > 0 && <svg {...getCardImageProps({ images })} />}
            <input {...getCardNumberProps({ onChange: (event) => setCardNumber(event.target.value) })} />
            <input {...getExpiryDateProps({ onChange: (event) => setExpirationDate(event.target.value) })} />
            <input {...getCVCProps({ onChange: (event) => setCvc(event.target.value) })} />
        </PaymentInputsWrapper>;

    return (
        <AppDialog
            open={open}
            onClose={closeModal}
            title={'Pay your balance'}
            onSubmit={handlePay}
            submitText={`Pay ${Constants.USDollar.format(amount)}`}
            submitDisabled={loading || !canSubmit}
            busy={paying}
        >
            {loading
                ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
                : <Stack spacing={2}>
                    <Box>
                        <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                            Amount due
                        </Typography>
                        <Typography variant={'h5'} sx={{ fontWeight: 700 }}>
                            {Constants.USDollar.format(amount)}
                        </Typography>
                        {statement?.isPastDue && statement.currentDue > 0 &&
                            <Typography variant={'body2'} sx={{ color: 'text.secondary', marginTop: '4px' }}>
                                {`${Constants.USDollar.format(statement.pastDue)} overdue, `}
                                {`${Constants.USDollar.format(statement.currentDue)} for this month.`}
                            </Typography>
                        }
                    </Box>

                    <Divider />

                    {error && <Alert severity={'error'}>{error}</Alert>}

                    <Typography variant={'subtitle2'}>Pay with</Typography>

                    <RadioGroup
                        value={selectedOption ?? ''}
                        onChange={(event) => {
                            const value = event.target.value;

                            setSelectedOption(value === String(NEW_PAYMENT_METHOD_ID)
                                ? NEW_PAYMENT_METHOD_ID
                                : value);
                        }}
                    >
                        {paymentOptions.map((paymentOption, index) =>
                            <FormControlLabel
                                className={paymentOption.isExpired ? 'expired-card-option' : ''}
                                key={index}
                                value={paymentOption.paymentProfileId}
                                control={<Radio disabled={paymentOption.isExpired} />}
                                label={`${paymentOption.cardType} - ${paymentOption.cardNumber}${paymentOption.isExpired ? ' - EXPIRED' : ''}`}
                            />
                        )}

                        <FormControlLabel
                            control={<Radio />}
                            label={'Use a different card'}
                            value={NEW_PAYMENT_METHOD_ID}
                        />

                        {isNewCardSelected &&
                            <Box sx={{ paddingLeft: '32px', paddingTop: '8px' }}>
                                {newCardInputs}
                                <FormControlLabel
                                    sx={{ marginTop: '8px' }}
                                    control={
                                        <Checkbox
                                            checked={saveAsDefault}
                                            onChange={(event) => setSaveAsDefault(event.target.checked)}
                                        />
                                    }
                                    label={'Also use this card for my monthly rent'}
                                />
                            </Box>
                        }
                    </RadioGroup>
                </Stack>
            }
        </AppDialog>
    );
}

PayBalanceModal.propTypes = {
    open: PropTypes.bool.isRequired,
    close: PropTypes.func.isRequired,

    /** The tenant's statement, which supplies the amount and the breakdown. */
    statement: PropTypes.shape({
        balance: PropTypes.number,
        pastDue: PropTypes.number,
        currentDue: PropTypes.number,
        isPastDue: PropTypes.bool,
    }),

    userId: PropTypes.number.isRequired,
    isMobile: PropTypes.bool.isRequired,
    setSnackbarProperties: PropTypes.func.isRequired,

    /** Called after a successful payment so the caller can reload the balance. */
    onPaid: PropTypes.func.isRequired,
};
